// src/pages/admin/LeadsPage.js - משופר עם נאבבר חדש
import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { 
  Loader, AlertCircle, Phone, User, Filter, MessageCircle, 
  Calendar, Trash2, ArrowUpDown, Users, TrendingUp,
  Mail, Clock
} from 'lucide-react';
import AdminNavbar from '../../components/admin/AdminNavbar';

const StatsCard = ({ icon: Icon, title, value, subtitle, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600 border-blue-200",
    green: "bg-green-50 text-green-600 border-green-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200",
    orange: "bg-orange-50 text-orange-600 border-orange-200"
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    // בדוק אימות
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        loadLeads();
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadLeads = async () => {
    try {
      const leadsSnapshot = await getDocs(collection(db, 'leads'));
      const leadsData = leadsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt || new Date()
      }));
      setLeads(leadsData);
    } catch (err) {
      setError('אירעה שגיאה בטעינת הנתונים');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLeads = filterCategory 
    ? leads.filter(lead => lead.category === filterCategory)
    : leads;

  const sortedLeads = [...filteredLeads].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'לא צוין';
    const phoneString = String(phone);
    return phoneString.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  };

  const formatDate = (date) => {
    if (!date) return 'לא זמין';
    const d = new Date(date);
    return d.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async (leadId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק פנייה זו?')) {
      try {
        await deleteDoc(doc(db, 'leads', leadId));
        setLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));
      } catch (err) {
        setError('אירעה שגיאה במחיקת הפנייה');
        console.error(err);
      }
    }
  };

  // סטטיסטיקות
  const stats = {
    total: leads.length,
    today: leads.filter(lead => {
      const today = new Date();
      const leadDate = new Date(lead.createdAt);
      return leadDate.toDateString() === today.toDateString();
    }).length,
    thisWeek: leads.filter(lead => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(lead.createdAt) >= weekAgo;
    }).length,
    categories: [...new Set(leads.map(lead => lead.category))].length
  };

  const uniqueCategories = [...new Set(leads.map(lead => lead.category))].filter(Boolean);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="flex items-center justify-center h-96">
          <Loader className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* כותרת */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ניהול פניות</h1>
          <p className="text-gray-600">צפייה וניהול כל הפניות מהאתר</p>
        </div>

        {/* סטטיסטיקות */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            icon={Users}
            title="סה״כ פניות"
            value={stats.total}
            subtitle="כל הזמנים"
            color="blue"
          />
          <StatsCard
            icon={TrendingUp}
            title="פניות היום"
            value={stats.today}
            subtitle="24 שעות אחרונות"
            color="green"
          />
          <StatsCard
            icon={Clock}
            title="השבוע"
            value={stats.thisWeek}
            subtitle="7 ימים אחרונים"
            color="purple"
          />
          <StatsCard
            icon={Filter}
            title="קטגוריות"
            value={stats.categories}
            subtitle="סוגי פניות שונים"
            color="orange"
          />
        </div>

        {/* כלי בקרה */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-600" />
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">כל הקטגוריות</option>
                  {uniqueCategories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              
              <button 
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')} 
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              >
                <ArrowUpDown className="w-4 h-4" />
                {sortOrder === 'desc' ? 'מהחדש לישן' : 'מהישן לחדש'}
              </button>
            </div>
            
            <div className="text-sm text-gray-600">
              מציג {sortedLeads.length} מתוך {leads.length} פניות
            </div>
          </div>
        </div>

        {/* טבלת פניות */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {sortedLeads.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">אין פניות להצגה</h3>
              <p className="text-gray-500">הפניות יופיעו כאן כשיגיעו מהאתר</p>
            </div>
          ) : (
            <>
              {/* תצוגת דסקטופ */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        פרטי קשר
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        קטגוריה ונושא
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        הודעה
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        תאריך
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        פעולות
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedLeads.map(lead => (
                      <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">
                                {lead.name || 'לא צוין'}
                              </span>
                            </div>
                            {lead.phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-gray-400" />
                                <a 
                                  href={`tel:${lead.phone}`}
                                  className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                                  dir="ltr"
                                >
                                  {formatPhoneNumber(lead.phone)}
                                </a>
                              </div>
                            )}
                            {lead.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-gray-400" />
                                <a 
                                  href={`mailto:${lead.email}`}
                                  className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                  {lead.email}
                                </a>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            {lead.category && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                {lead.category}
                              </span>
                            )}
                            {lead.subject && (
                              <p className="text-sm text-gray-900 font-medium">{lead.subject}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="max-w-xs">
                            <p className="text-sm text-gray-600 truncate">
                              {lead.message || 'אין הודעה'}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {formatDate(lead.createdAt)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {lead.phone && (
                              <a 
                                href={`https://wa.me/${lead.phone.replace(/[^\d]/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 hover:bg-green-200 text-green-700 text-xs rounded-md transition-colors"
                              >
                                <MessageCircle className="w-3 h-3" />
                                וואטסאפ
                              </a>
                            )}
                            <button
                              onClick={() => handleDelete(lead.id)}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs rounded-md transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                              מחק
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* תצוגת מובייל */}
              <div className="lg:hidden divide-y divide-gray-200">
                {sortedLeads.map(lead => (
                  <div key={lead.id} className="p-6 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {lead.name || 'לא צוין'}
                          </span>
                        </div>
                        {lead.category && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {lead.category}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(lead.createdAt)}
                      </div>
                    </div>

                    {lead.subject && (
                      <p className="text-sm font-medium text-gray-900">{lead.subject}</p>
                    )}

                    {lead.message && (
                      <p className="text-sm text-gray-600">{lead.message}</p>
                    )}

                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                      {lead.phone && (
                        <>
                          <a 
                            href={`tel:${lead.phone}`}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs rounded-md transition-colors"
                          >
                            <Phone className="w-3 h-3" />
                            {formatPhoneNumber(lead.phone)}
                          </a>
                          <a 
                            href={`https://wa.me/${lead.phone.replace(/[^\d]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 hover:bg-green-200 text-green-700 text-xs rounded-md transition-colors"
                          >
                            <MessageCircle className="w-3 h-3" />
                            וואטסאפ
                          </a>
                        </>
                      )}
                      {lead.email && (
                        <a 
                          href={`mailto:${lead.email}`}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-md transition-colors"
                        >
                          <Mail className="w-3 h-3" />
                          מייל
                        </a>
                      )}
                      <button
                        onClick={() => handleDelete(lead.id)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs rounded-md transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                        מחק
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadsPage;